import React, {
  useEffect,
  SyntheticEvent,
  useMemo,
  useCallback,
  memo,
  useRef,
} from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import * as Yup from 'yup';
import { Formik, FormikHelpers, FormikConsumer } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PaneLayout, useParticipantDisplayName } from '@app/core';

import { Theme } from '@app/theme';
import {
  createParticipantsInPanel,
  createParticipantByIdSelector,
  updateParticipant,
  ParticipantTag,
  selectCurrentPanel,
  Devices,
} from 'store';
import { Participant } from '@app/api';

import { SubmitButton, SquareButton, TitleWithIcon, Toggle } from 'components';
import { hideCrisp, showCrisp } from '@app/analytics';
import { useFormikDispatchSubmit } from 'hooks/useFormikSubmit';
import { YupValidators } from 'lib/validators';
import { clientSpecialConfigs } from 'config/clientSpecialConfigs';
import {
  useParticipantFieldsByCategory,
  useParticipantFieldsCategories,
} from '@app/api';
import { getParticipantFieldInput } from '@app/participant-fields';

const useStyles = createUseStyles((theme: Theme) => ({
  titleLine: {
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    ...theme.fonts.h4,
    flex: 1,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.marginBase,
  },
  section: {
    marginTop: theme.marginBase * 5,
  },
}));

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  tags: [] as ParticipantTag[],
  city: '',
  postalCode: '',
  genderText: '',
  jobTitle: '',
  birthdate: '',
  saveAndSubmit: false,
  devices: [] as Devices[],
};

type Values = typeof initialValues;

interface Props {
  onRequestClose?: () => any;
  isOpen?: boolean;
  panelId?: string;
  participantId?: string;
  deleteParticipant: (id: string) => any;
}

const participantToValues = (participant?: Participant) => {
  return participant
    ? {
        ...initialValues,
        ...participant,
        phoneNumber: participant.phoneNumber || initialValues.phoneNumber,
        tags: participant.tags || initialValues.tags,
      }
    : initialValues;
};

export const ParticipantPane = memo((props: Props) => {
  const { participantId, onRequestClose, panelId, deleteParticipant } = props;
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const participant = useSelector(createParticipantByIdSelector(participantId));

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string().required(
          t('participants.pane.requiredFirstName'),
        ),
        lastName: Yup.string().nullable(),
        email: Yup.string().nullable().email().required(),
        phoneNumber: YupValidators.isPhoneNumber().nullable(),
        country: Yup.object().shape({ key: Yup.string(), name: Yup.string() }),
        city: Yup.string().nullable(),
        postalCode: Yup.string().nullable(),
        genderText: Yup.string().nullable(),
        gender: Yup.string().nullable(),
        jobTitle: Yup.string().nullable(),
        birthdate: YupValidators.isDateValid().nullable(),
      }),
    [t],
  );

  const panel = useSelector(selectCurrentPanel);
  const participantFields = useParticipantFieldsByCategory();
  const { data: categories = [] } = useParticipantFieldsCategories();

  const specialConfigs = useMemo(
    () =>
      clientSpecialConfigs.filter(
        (config) => config.orgId === (panel && panel.organizationId),
      ),
    [panel],
  );

  const { isOpen } = props;
  useEffect(() => {
    if (isOpen) {
      hideCrisp();
      return () => showCrisp();
    } else {
      showCrisp();
      return;
    }
  }, [isOpen]);

  const thunk = useCallback(
    ({ saveAndSubmit, ...values }: Values) => {
      if (!panelId) {
        return;
      }

      const ppt = {
        ...values,
        phoneNumber: values.phoneNumber ? values.phoneNumber : null,
        birthdate: values.birthdate ? values.birthdate : undefined,
        devices:
          values.devices && Array.isArray(values.devices)
            ? values.devices
            : undefined,
      };

      if (participantId) {
        return updateParticipant(participantId, ppt);
      }

      return createParticipantsInPanel(ppt, panelId);
    },
    [panelId, participantId],
  );
  const onSuccess = useCallback(
    (helpers: FormikHelpers<Values>, values: Values) => {
      const { saveAndSubmit } = values;
      helpers.resetForm();
      if (onRequestClose && !saveAndSubmit) onRequestClose();
    },
    [onRequestClose],
  );
  const onSubmit = useFormikDispatchSubmit(thunk, { onSuccess });
  const _deleteParticipant = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!participantId) return;
      deleteParticipant(participantId);
    },
    [deleteParticipant, participantId],
  );

  const formikInitialValues = useMemo(
    () => participantToValues(participant),
    [participant],
  );

  const firstInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    setTimeout(() => {
      if (isOpen && firstInputRef.current) {
        firstInputRef.current.focus();
      }
    }, 300);
  }, [isOpen, participantId]);

  const formRef = useRef<HTMLInputElement | null>(null);
  const name = useParticipantDisplayName(participant, true);

  return (
    <Formik
      key={`${isOpen} ${participantId}`}
      initialValues={formikInitialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <PaneLayout
        submitShadow
        submitSectionContent={
          <FormikConsumer>
            {({ handleSubmit, setFieldValue }) => (
              <div className={classes.buttonContainer}>
                {!participantId && (
                  <SubmitButton
                    color="green"
                    small
                    variant="muted"
                    onClick={(e: SyntheticEvent) => {
                      e.preventDefault();
                      setFieldValue('saveAndSubmit', true);
                      handleSubmit();
                      if (formRef && formRef.current) {
                        formRef.current.scrollIntoView({
                          block: 'start',
                        });
                      }
                    }}
                  >
                    <Trans i18nKey="participants.pane.submitAndNew" />
                  </SubmitButton>
                )}
                {!!participantId && (
                  <SquareButton
                    role="_button"
                    color="red"
                    variant="muted"
                    icon="ri-delete-bin-line"
                    onClick={_deleteParticipant}
                  />
                )}
                <SubmitButton color="green" small>
                  <Trans i18nKey="participants.pane.submit" />
                </SubmitButton>
              </div>
            )}
          </FormikConsumer>
        }
      >
        <div className={classes.titleLine}>
          <h3 className={classes.title}>
            {name ? name : <Trans i18nKey="participants.pane.title" />}
          </h3>
          <SquareButton
            icon="ri-close-line"
            color="ink"
            onClick={onRequestClose}
          />
        </div>
        {categories.map((category, cIndex) => (
          <section className={classes.section}>
            <TitleWithIcon
              title={category.titleClient || category.title}
              icon={category.icon}
              iconColor={category.iconColor}
              key={category.name}
            />
            {participantFields[category.name] &&
              participantFields[category.name].map((field, fIndex) => {
                const ParticipantInput = getParticipantFieldInput(field);
                if (!ParticipantInput) {
                  return null;
                }
                return (
                  <ParticipantInput
                    field={field}
                    label={field.label}
                    panelId={panelId}
                    disabled={!!field.readOnly}
                    ref={
                      cIndex === 0 && fIndex === 0 ? firstInputRef : undefined
                    }
                  />
                );
              })}
          </section>
        ))}
        <Toggle name="excluded" label={t('participants.display.excluded')} />
        {specialConfigs &&
          specialConfigs.map((config: any) =>
            config.specialFields.map((Field: any) => (
              <Field.formComponent
                name={Field.name}
                label={Field.clientLabel}
              />
            )),
          )}
      </PaneLayout>
    </Formik>
  );
});
